import {BasicDataTable, BasicDataTableConfig} from "@/shared/ui/basicDataTable";
import {RecordingGpsPositionPopover} from "@/shared/recording/recordingGpsPositionPopover";
import {formatDuration, formatRunningDurationSince} from "@/shared/utils/datefunctions";
import {DashboardDetailsEntry, GPSPosition, RunningEntry, Task} from "@/shared/timr";
import {DeletedStatus} from "@/shared/timrTypes";
import {renderProgress} from "@/features/reports/progressBarHelper";

export interface FooterDefinition<TResponse> {
	[footerColumn: string]: (json: TResponse) => string;
}

export interface DashboardTableConfig<TRow = unknown> extends BasicDataTableConfig {
	onRowClick?: (data: TRow) => void;
	isRowClickable?: (data: TRow) => boolean;
	detailsPopover?: boolean;
	presentation?: DashboardTableBarPresentation;
	pinRecord?: (record: TRow) => boolean;
	customFooter?: boolean;
}

export enum DashboardTableBarPresentation {
	Absolute, Relative
}

/**
 * Dashboard tables receive an Ajax response shaped `TResponse` and render rows of
 * type `TRow` extracted from it via DataTables' `ajax.dataSrc` (e.g. `"users.results"`).
 *
 * - `TRow`: shape of one row (e.g. `UserDashboardEntry`).
 * - `TResponse`: shape of the full server response (e.g. `UserDashboardResponse`),
 *   used by `FooterDefinition` and the `dataChanged` event.
 */
export abstract class DashboardTable<TRow extends object, TResponse> extends BasicDataTable<TRow> {
	protected presentation = DashboardTableBarPresentation.Relative;

	private customFooter: boolean;

	protected constructor(url: string, public config: DashboardTableConfig<TRow>) {
		super(url, config);

		this.customFooter = config.customFooter;

		if (config.detailsPopover) {
			// eslint-disable-next-line @typescript-eslint/no-this-alias
			const that = this;
			this.config.element.parent().popover({
				trigger: "hover",
				selector: "tbody > tr",
				container: ".content-wrapper",
				delay: {show: 500, hide: 0},
				placement: "auto top",
				html: true,
				template: `<div class="popover user-times-popover" role="tooltip">
									<div class="arrow"></div>
									<div class="popover-content"></div>
								</div>`,
				content: function (this: HTMLElement) {
					const tr = $(this);
					const recording = that.getRowData(tr);
					if (recording === undefined) {
						return null;
					}
					return that.renderDetailsPopover(recording);
				}
			});
		}

		this.dataTable.on("xhr", (event, settings, json, xhr) => {
			this.config.element.find("tr").popover("hide");
		});

		const footerDefinition = this.createFooterDefinition();
		if (footerDefinition) {
			this.onDataChanged<TResponse>(json => {
				Object.keys(footerDefinition).forEach(footerColumn => {
					const footerContent = footerDefinition[footerColumn](json);
					if (this.customFooter) {
						const splits = footerContent.split(";");
						$(this.dataTable.column(footerColumn).footer()).html((splits[0] ? splits[0] : "") + "<br>" + (splits[1] ? splits[1] : "&nbsp;"));
					} else {
						$(this.dataTable.column(footerColumn).footer()).html(footerContent);
					}
				});
			});
		}

		if (config.presentation !== undefined) {
			this.presentation = config.presentation;
		}

		if (config.pinRecord) {
			this.dataTable.on("draw.dt", (event, s) => {

				$(event.target).find("tbody > tr")
					.toArray()
					.map(tr => $(tr))
					.forEach(tr => {
						const record = this.getRowData(tr);

						if (record && config.pinRecord(record)) {
							tr.addClass("dashboard-pinned-record");
						}
					});
			});
		}
	}

	protected createDataTableConfig(): DataTables.Settings {
		return {
			dom: `rt`,
			paging: false,
			serverSide: false,
			rowCallback: (row, data, index) => {
				const rowData = data as TRow;
				if (this.config.onRowClick) {
					$(row).off("click");
					$(row).click((e) => {
						this.config.onRowClick(rowData);
					});
				}

				if (this.config.isRowClickable) {
					const clickable = this.config.isRowClickable(rowData);
					$(row).toggleClass("clickable", clickable);
				}
			}
		};
	}

	public setPresentation(presentation: DashboardTableBarPresentation) {
		this.presentation = presentation;
		this.dataTable.rows().invalidate("data").draw();
	}

	protected renderDetailsPopover(entry: DashboardDetailsEntry, showProjectTimeStatus: boolean = false) {
		const tableBody = $(`<table class="table table-borderless" style="width: 100%"><tbody></tbody></table>`);

		const workingTimeCategories = entry.workingTimeCategories;
		if (globals.workingTimeEnabled && workingTimeCategories) {
			tableBody.append(this.renderDetailsPopoverRow(
				messages["report.column.workingTime"],
				null,
				entry.workingTimeDuration,
				1.0,
				null,
				true,
				true));

			if (workingTimeCategories.length === 0) {
				tableBody.append(this.renderDetailsPopoverRow(
					messages["statisticsPanel.record.empty"],
					null,
					0,
					1.0,
					null));
			}
			workingTimeCategories.forEach(category => {
				const categoryScale = category.duration / entry.workingTimeDuration;

				tableBody.append(this.renderDetailsPopoverRow(
					messages["workingTimeTypeCategory." + category.category],
					renderProgress({
						css: "progress-bar-workingTimeCategory-" + category.category,
						progress: categoryScale
					}),
					category.duration,
					categoryScale,
					null));
			});

		}
		if (globals.workingTimeEnabled && globals.projectTimeEnabled && workingTimeCategories) {
			tableBody.append($(`<tr colspan="6" class="table-row-spacer"></tr>`));
		}
		// projectTime
		if (globals.projectTimeEnabled) {
			tableBody.append(this.renderDetailsPopoverRow(
				messages["report.column.projectTime"],
				null,
				entry.duration,
				entry.duration / entry.workingTimeDuration,
				1.0,
				true,
				true));

			const durationBillableScale = entry.durationBillable / entry.duration;
			tableBody.append(this.renderDetailsPopoverRow(
				messages["report.column.billable"],
				renderProgress({
					progress: durationBillableScale,
					css: "progress-bar-projectTime-billable"
				}),
				entry.durationBillable,
				null,
				durationBillableScale
			));

			const durationNotBillableScale = entry.durationNotBillable / entry.duration;
			tableBody.append(this.renderDetailsPopoverRow(
				messages["report.column.notBillable"],
				renderProgress({
					progress: durationNotBillableScale,
					css: "progress-bar-projectTime-notBillable"
				}),
				entry.durationNotBillable,
				null,
				durationNotBillableScale,
				showProjectTimeStatus
			));
			if (showProjectTimeStatus && entry.statuses) {
				entry.statuses.forEach(status => {
					const durationScale = status.duration / entry.duration;
					tableBody.append(this.renderDetailsPopoverRow(
						messages["reports.filter.status." + status.status],
						renderProgress({
							progress: durationScale,
							css: "progress-bar-grey"
						}),
						status.duration,
						null,
						durationScale
					));
				});
			}
		}

		return tableBody;
	}

	private renderDetailsPopoverRow(this: void, label: string, progressBar: string, duration: number, workingTimePercentage: number | null, projectTimePercentage: number | null, bottomBorder = false, isHeader = false) {
		if (!isHeader) {
			label = `<i class="icon-chevron-right"></i>` + label;
		}

		const workingTimePercentageText = workingTimePercentage && isFinite(workingTimePercentage)
			? (workingTimePercentage * 100).toFixed(2) + "%"
			: "";

		const projectTimePercentageText = projectTimePercentage && isFinite(projectTimePercentage)
			? (projectTimePercentage * 100).toFixed(2) + "%"
			: "";

		const table = $(`<tr class="${bottomBorder ? "border-bottom" : ""}">`)
			.append($(`<td style="width: 300px" class="${isHeader ? "text-bold" : ""}">`).html(label))
			.append($("<td style='width: 200px'>").html(progressBar))
			.append($("<td class='border-left text-right'>").text(formatDuration(duration)));

		if (globals.workingTimeEnabled) {
			table.append($("<td class='border-left text-right'>").text(workingTimePercentageText));
		}

		if (globals.projectTimeEnabled) {
			table.append($("<td class='border-left text-right'>").text(projectTimePercentageText));
		}

		return table;
	}

	protected renderDurationSince(this: void, data: string, type: string, recording: RunningEntry, meta: DataTables.CellMetaSettings): string {
		if (data) {
			return formatRunningDurationSince(recording);
		} else {
			return "";
		}
	}

	protected createFooterDefinition(): FooterDefinition<TResponse> {
		return undefined;
	}

	protected initRunningRecordingGpsPositionPopover() {
		new RecordingGpsPositionPopover({
			baseElement: this.config.element,
			selector: "td.gps-position a",
			dataTable: this.dataTable,
			isRunningRecording: true
		});
		this.config.element.on("click", "td.gps-position > a", (e) => {
			const gpsPositionCell = this.dataTable.cell($(e.target).closest("td.gps-position"));
			if (gpsPositionCell.length > 0) {
				this.showPositionOnMap(gpsPositionCell.data() as GPSPosition);
			}
		});
	}

	private showPositionOnMap(data: GPSPosition) {
		if (data.deletedStatus === DeletedStatus.NOT_DELETED) {
			const mapsWindow = window.open("https://maps.google.com/?q=" + data.latitude + "," + data.longitude, "googlemaps", "status=0,toolbar=0,menubar=0,location=0");
			mapsWindow.focus();
		}
	}
}

function sortFunction<K extends number | string>(api: DataTables.Api, selectedTaskId: number, col: number, minValueOfType: K, maxValueOfType: K) {
	const column = api.column(col, {order: "index"});

	const tableOrder = api.order().find(sortedOrder => sortedOrder[0] === col);
	const columnOrder = tableOrder ? tableOrder[1] : "asc";

	const sort = column.nodes().map((td, i) => {
		const task = api.row(i).data() as Task;
		const data = column.data()[i] as K;

		if (selectedTaskId && task.id === selectedTaskId) {
			return columnOrder === "asc" ? minValueOfType : maxValueOfType;
		}

		return data;
	});

	return sort;
}

export function registerSortingFunctions(selectedTaskIdProvider: () => number) {
	type OrderPlugin = (this: JQuery & { api(): DataTables.Api }, settings: DataTables.Settings, col: number) => DataTables.Api;
	const orderExt = $.fn.dataTable.ext.order as Record<string, OrderPlugin>;

	orderExt["task-dashboard-text"] = function (settings, col) {
		// space is smaller than all letters and special characters, Z is bigger than all others (including small letters)
		return sortFunction(this.api(), selectedTaskIdProvider(), col, "          ", "ZZZZZZZZZZ");
	};

	orderExt["task-dashboard-duration"] = function (settings, col) {
		return sortFunction(this.api(), selectedTaskIdProvider(), col, -Number.MAX_VALUE, Number.MAX_VALUE);
	};
}
